import { computed } from 'vue'
import { useI18n, useProduct, useRuntimeConfig } from '#imports'

// The working days behind each option, identical across all locale files.
const daysByOption = {
    option1: { min: 1, max: 3 },
    option2: { min: 2, max: 4 },
    option3: { min: 3, max: 5 },
}

// Keyed on the channel's country: the window follows the market, not the visitor's language.
// Every country not listed gets option3.
const optionByCountry: Record<string, keyof typeof daysByOption> = {
    DE: 'option1',
    NL: 'option1',
    AT: 'option2',
    BE: 'option2',
    CH: 'option2',
    DK: 'option2',
    IT: 'option2',
    PL: 'option2',
    SE: 'option2',
}

/**
 * The delivery time as the product detail page states it. Shopware's own `deliveryTime` only
 * reaches the page for dropship products; everything else follows the market.
 */
export function useProductDeliveryTime () {
    const { product } = useProduct()
    const { t } = useI18n()
    const runtimeConfig = useRuntimeConfig()

    const option = computed<keyof typeof daysByOption>(() => {
        const countryIso = (runtimeConfig.public?.platformFulfillment as { countryIso: string | null } | undefined)?.countryIso
        return optionByCountry[countryIso ?? ''] ?? 'option3'
    })

    const isVisible = computed(() => {
        const hasDeliveryTime = product.value.deliveryTime != null
        const isCloseoutAvailable = product.value.isCloseout && product.value.stock >= (product.value.minPurchase || 1)
        const isTitleistLogoball = !!product.value.customFields?.titleist_logoball

        return hasDeliveryTime && (isCloseoutAvailable || !product.value.isCloseout || isTitleistLogoball)
    })

    // Preorder, Logoball and dropship products state their own time, as free text.
    const isMarketDefault = computed(() => !(
        product.value.customFields?.pre_order_delivery_time
        || product.value.customFields?.titleist_logoball
        || product.value.customFields?.dropship
        || product.value.extensions?.courseframe?.courseSelection
    ))

    const displayText = computed(() => {
        // Preorder products have their own delivery time with highest priority
        if (product.value.customFields?.pre_order_delivery_time) {
            return product.value.customFields?.pre_order_delivery_time
        }

        // Titleist Logoball products have fixed delivery time
        if (product.value.customFields?.titleist_logoball && !product.value.customFields?.dropship) {
            return `${t('product.detail.buyBox.deliveryTime')} ${t('product.detail.buyBox.titleistWeeks')}`
        }

        // Dropship products show raw delivery time
        if (product.value.customFields?.dropship || product.value.extensions?.courseframe?.courseSelection) {
            return product.value.deliveryTime?.translated?.name
        }

        // Standard products show localized delivery time with market-specific option
        return `${t('product.detail.buyBox.deliveryTime')} ${t(`product.detail.buyBox.deliveryTimeOptions.${option.value}`)}`
    })

    // Only where displayText is the market default, so a free text is never turned into numbers.
    const days = computed(() => (isVisible.value && isMarketDefault.value ? daysByOption[option.value] : undefined))

    return {
        isVisible,
        displayText,
        days,
    }
}
